import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Switch,
  Button,
  IconButton,
  Paper,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { createSeasonAction, clearCreateSeasonAction, getAllSeasonsAction, updateSeasonAction, clearUpdateSeasonAction } from "../../store/actions";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";


export default function SeasonInformation() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { successData, error, errorInfo } = useSelector((state) => state.createSeasonReducer);
  const { successData: seasonsData } = useSelector((state) => state.getAllSeasonsReducer || {});
  const seasonId = location.state?.seasonId;
  const mode = location.state?.mode;

  const [formPopulated, setFormPopulated] = useState(false);
  const { successData: updateSuccess, error: updateError, errorInfo: updateErrorInfo } = useSelector((state) => state.updateSeasonReducer || {});

  const validationSchema = Yup.object({
    seasonName: Yup.string()
      .oneOf(["KHARIF", "RABI", "SPRING"], "Invalid season")
      .required("Season is required"),
    year: Yup.string()
      .matches(/^\d{4}-\d{2}$/, "Year must be in YYYY-YY format")
      .required("Year is required"),
    isCurrent: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      seasonName: "",
      year: "",
      isCurrent: false,
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      const payload = {
        name: values.seasonName.trim(),
        year: values.year.trim(),
        isCurrent: values.isCurrent,
      };
      if (mode === 'edit' && seasonId) {
        dispatch(updateSeasonAction({ ...payload, endPoint: `season/${seasonId}` }));
      } else {
        dispatch(createSeasonAction(payload));
      }
    }
  });

  useEffect(() => {
    if (mode === 'edit' && seasonId) {
      setFormPopulated(false);
      dispatch(getAllSeasonsAction({ endPoint: `season/${seasonId}` }));
    }
  }, [dispatch, mode, seasonId]);

  useEffect(() => {
    if (mode === 'edit' && seasonsData && !formPopulated) {
      const payload = seasonsData?.data?.season || seasonsData?.data || null;
      const payloadId = payload?.seasonId;
      if (payload && (!seasonId || payloadId === seasonId)) {
        formik.setValues({
          seasonName: payload.name || "",
          year: payload.year || "",
          isCurrent: !!payload.isCurrent,
        });
        setFormPopulated(true);
      }
    }
  }, [mode, seasonsData, formPopulated, seasonId, formik]);

  useEffect(() => {
    if (successData) {
      const message = successData?.message || "Season created successfully";
      toast.success(message);
      dispatch(clearCreateSeasonAction());
      navigate('/masterData/seasons');

    }
  }, [successData, dispatch]);

  useEffect(() => {
    if (error) {
      const errMsg = errorInfo || "Failed to create season";
      toast.error(errMsg);
      dispatch(clearCreateSeasonAction());
    }
  }, [error, errorInfo, dispatch]);

  useEffect(() => {
    if (updateSuccess) {
      const message = updateSuccess?.message || "Season updated successfully";
      toast.success(message);
      dispatch(clearUpdateSeasonAction());
      navigate('/masterData/seasons');
    }
    if (updateError) {
      const errMsg = updateErrorInfo || "Failed to update season";
      toast.error(errMsg);
      dispatch(clearUpdateSeasonAction());
    }
  }, [updateSuccess, updateError, updateErrorInfo, dispatch, navigate]);


  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          width: '100%',
          boxSizing: 'border-box',
          p: { xs: 2, sm: 1, md: 4, lg: 1, xl: 2 },
          mt: { sm: 10 }
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Typography variant="h6" fontWeight={600}>
            Season Information
          </Typography>

          <IconButton size="small" onClick={() => navigate('/masterData/seasons')}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Season & Year */}
        <Box mb={3} display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={14} mb={1}>
              Season
            </Typography>
            <TextField
              fullWidth
              select
              label="Select Season"
              size="small"
              name="seasonName"
              value={formik.values.seasonName}
              onChange={formik.handleChange}
              required
              error={Boolean(formik.touched.seasonName && formik.errors.seasonName)}
              helperText={formik.touched.seasonName && formik.errors.seasonName}
            >
              <MenuItem value="">Select Season</MenuItem>
              <MenuItem value="KHARIF">KHARIF</MenuItem>
              <MenuItem value="RABI">RABI</MenuItem>
              <MenuItem value="SPRING">SPRING</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={14} mb={1}>
              Year
            </Typography>
            <TextField
              fullWidth
              placeholder="YYYY-YY"
              size="small"
              name="year"
              value={formik.values.year}
              onChange={formik.handleChange}
              error={Boolean(formik.touched.year && formik.errors.year)}
              helperText={formik.touched.year && formik.errors.year}
            />
          </Box>
        </Box>

        {/* Is Current */}
        <Box mb={4}>
          <Typography fontSize={14} mb={1}>
            Is Current
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <Switch checked={formik.values.isCurrent} onChange={(e) => formik.setFieldValue('isCurrent', e.target.checked)} color="success" />
            <Typography fontSize={14}>{formik.values.isCurrent ? "Yes" : "No"}</Typography>
          </Box>
        </Box>

        {/* Actions */}
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          sx={{

            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 }
          }}
        >
          <Button

            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              py: 1.2,
            }}
            onClick={() => navigate('/masterData/seasons')}
          >
            Cancel
          </Button>

          <Button

            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              py: 1.2,
              background: "#4F46E5",
            }}
            onClick={formik.handleSubmit}
          >
            {mode === 'edit' ? 'Update' : 'Submit'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}



